// ============================================
// Player Layout
// ============================================

import React from 'react';
import { Outlet, useParams } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { Event } from '../../types';
import InstallPrompt from '../../components/InstallPrompt';

export default function PlayerLayout() {
  const { eventId } = useParams<{ eventId: string }>();
  const { user, loading: authLoading, signInAsPlayer } = useAuth();

  const [event, setEvent] = React.useState<Event | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  // Auto sign-in anonymously if not authenticated
  React.useEffect(() => {
    if (!authLoading && !user) {
      console.log('No user, attempting anonymous sign-in...');
      signInAsPlayer().catch((err) => {
        console.error('Anonymous sign-in failed:', err);
        setError(`Failed to connect: ${err.code || err.message || 'Unknown error'}. Please try again.`);
      });
    }
  }, [user, authLoading, signInAsPlayer]);

  // Load event
  React.useEffect(() => {
    if (!eventId) {
      setError('Invalid event link');
      setLoading(false);
      return;
    }

    const unsubscribe = onSnapshot(
      doc(db, 'events', eventId),
      (docSnap) => {
        if (docSnap.exists()) {
          const eventData = { id: docSnap.id, ...docSnap.data() } as Event;
          if (eventData.status !== 'live') {
            setError('This event is not currently active');
          } else {
            setEvent(eventData);
          }
        } else {
          setError('Event not found');
        }
        setLoading(false);
      },
      (err) => {
        console.error('Error loading event:', err);
        setError('Failed to load event');
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [eventId]);

  if (loading || authLoading) {
    return (
      <div className="player-layout loading-screen">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="player-layout error-screen">
        <div className="error-container">
          <h1>Oops!</h1>
          <p>{error}</p>
          <button onClick={() => window.location.reload()} className="btn btn-primary">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!event) {
    return null;
  }

  return (
    <div className="player-layout">
      <header className="player-header">
        <h1>{event.name}</h1>
        <p>{event.venueName}</p>
      </header>

      <main className="player-main">
        <Outlet context={{ event }} />
      </main>

      <InstallPrompt variant="banner" />
    </div>
  );
}

// Hook to access event from nested components
export function usePlayerEvent() {
  const context = React.useContext(React.createContext<{ event: Event } | null>(null));
  return context;
}
